/**
 * Topology utilities for graph analysis.
 *
 * Provides functions to:
 * - Identify connected components (disconnected subgraphs)
 * - Find endpoint materials (roots with no incoming, leaves with no outgoing)
 * - Detect cycles via DFS reachability
 */

import type { GraphRecords, MaterialNodeSpec, FlowEdgeSpec } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// CONNECTED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

export interface ConnectedComponent {
  componentId: string;
  materialIds: Set<string>;
  operationIds: Set<string>;
}

/**
 * Find all connected components (disconnected subgraphs) in the workflow.
 *
 * A component is a maximal connected subgraph of materials and operations,
 * considering both flow edges and link edges.
 *
 * Algorithm: Union-Find using BFS from each unvisited node.
 */
export function getConnectedComponents(
  graphData: Pick<GraphRecords, 'materialNodes' | 'operationNodes' | 'flowEdges'>,
): ConnectedComponent[] {
  const { materialNodes, operationNodes, flowEdges } = graphData;

  // Build adjacency lists (undirected, ignoring edge direction for connectivity)
  const adjacency = new Map<string, Set<string>>();

  const allNodeIds = [
    ...Object.keys(materialNodes),
    ...Object.keys(operationNodes),
  ];

  for (const nodeId of allNodeIds) {
    adjacency.set(nodeId, new Set());
  }

  for (const edge of Object.values(flowEdges)) {
    const src = edge.sourceId;
    const tgt = edge.targetId;
    adjacency.get(src)?.add(tgt);
    adjacency.get(tgt)?.add(src);
  }

  // BFS to find components
  const visited = new Set<string>();
  const components: ConnectedComponent[] = [];

  for (const nodeId of allNodeIds) {
    if (visited.has(nodeId)) continue;

    const component: ConnectedComponent = {
      componentId: nodeId, // Use first node ID as component identifier
      materialIds: new Set(),
      operationIds: new Set(),
    };

    const queue = [nodeId];
    visited.add(nodeId);

    while (queue.length > 0) {
      const current = queue.shift()!;

      if (materialNodes[current]) {
        component.materialIds.add(current);
      } else if (operationNodes[current]) {
        component.operationIds.add(current);
      }

      for (const neighbor of adjacency.get(current) ?? []) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    components.push(component);
  }

  return components;
}

// ─────────────────────────────────────────────────────────────────────────────
// ENDPOINT DETECTION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if a material is a root endpoint (no incoming flow or link edges).
 * Root materials can be sources for outgoing links.
 */
export function isRootMaterial(
  materialId: string,
  flowEdges: Record<string, FlowEdgeSpec>,
): boolean {
  return !Object.values(flowEdges).some(
    (edge) => edge.targetId === materialId && edge.edgeKind !== 'graphsLink',
  );
}

/**
 * Check if a material is a leaf endpoint (no outgoing flow or link edges).
 * Leaf materials can be targets for incoming links.
 */
export function isLeafMaterial(
  materialId: string,
  flowEdges: Record<string, FlowEdgeSpec>,
): boolean {
  return !Object.values(flowEdges).some(
    (edge) => edge.sourceId === materialId && edge.edgeKind !== 'graphsLink',
  );
}

/**
 * Check if a material is a reminder node (direct output from a COMPLEX operation).
 * Reminder nodes should NOT be linkable as sources.
 */
export function isReminderNode(
  materialId: string,
  flowEdges: Record<string, FlowEdgeSpec>,
  operationNodes: Record<string, any>,
  materialNodes: Record<string, MaterialNodeSpec>,
): boolean {
  const material = materialNodes[materialId];
  return material?.isReminder === true;
}

/**
 * Get all root materials (no incoming flow edges) in a connected component.
 */
export function getRootMaterials(
  componentMaterialIds: Set<string>,
  flowEdges: Record<string, FlowEdgeSpec>,
  materialNodes: Record<string, MaterialNodeSpec>,
): MaterialNodeSpec[] {
  return Array.from(componentMaterialIds)
    .filter((matId) => isRootMaterial(matId, flowEdges))
    .map((matId) => materialNodes[matId])
    .filter((m) => m != null);
}

/**
 * Get all leaf materials (no outgoing flow edges) in a connected component.
 */
export function getLeafMaterials(
  componentMaterialIds: Set<string>,
  flowEdges: Record<string, FlowEdgeSpec>,
  materialNodes: Record<string, MaterialNodeSpec>,
): MaterialNodeSpec[] {
  return Array.from(componentMaterialIds)
    .filter((matId) => isLeafMaterial(matId, flowEdges))
    .map((matId) => materialNodes[matId])
    .filter((m) => m != null);
}

// ─────────────────────────────────────────────────────────────────────────────
// CYCLE DETECTION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if creating a directed edge from `fromNodeId` to `toNodeId` would create a cycle.
 *
 * Algorithm: DFS from `toNodeId` following outgoing edges. If we reach `fromNodeId`,
 * then `toNodeId` already has a path to `fromNodeId`, so adding the reverse edge
 * would create a cycle.
 *
 * Note: Considers both flow edges and link edges (edgeKind agnostic).
 */
export function wouldCreateCycle(
  graphData: Pick<GraphRecords, 'flowEdges'>,
  fromNodeId: string,
  toNodeId: string,
): boolean {
  const { flowEdges } = graphData;

  // DFS from toNodeId following outgoing edges
  const visited = new Set<string>();
  const stack = [toNodeId];

  while (stack.length > 0) {
    const current = stack.pop()!;

    if (current === fromNodeId) {
      // Found a path from toNodeId to fromNodeId → cycle exists
      return true;
    }

    if (visited.has(current)) {
      continue;
    }

    visited.add(current);

    // Add all outgoing neighbors to stack
    for (const edge of Object.values(flowEdges)) {
      if (edge.sourceId === current && !visited.has(edge.targetId)) {
        stack.push(edge.targetId);
      }
    }
  }

  return false;
}

/**
 * Find candidate link targets for a given material endpoint.
 *
 * For a root material in component A: return leaf materials from other components
 * For a leaf material in component A: return root materials from other components
 *
 * Note: Reminder leaves CAN be linked TO. Only reminder nodes AS SOURCE are blocked.
 * This prevents self-linking and ensures valid link topology.
 */
export function getLinkCandidates(
  materialId: string,
  graphData: Pick<
    GraphRecords,
    'materialNodes' | 'operationNodes' | 'flowEdges'
  >,
): MaterialNodeSpec[] {
  const { materialNodes, operationNodes, flowEdges } = graphData;

  // Determine if this material is a root or leaf
  const isRoot = isRootMaterial(materialId, flowEdges);
  const isLeaf = isLeafMaterial(materialId, flowEdges);

  if (!isRoot && !isLeaf) {
    // Not an endpoint → no valid link candidates
    return [];
  }

  // Find which component this material belongs to
  const components = getConnectedComponents({ materialNodes, operationNodes, flowEdges });
  const currentComponentId = components.find((c) =>
    c.materialIds.has(materialId),
  )?.componentId;

  if (!currentComponentId) {
    return [];
  }

  // Collect candidates from other components
  const candidates: MaterialNodeSpec[] = [];

  for (const component of components) {
    if (component.componentId === currentComponentId) {
      continue; // Skip same component
    }

    if (isRoot) {
      // Root → link to leaves of other components (excluding reminder nodes)
      const leaves = getLeafMaterials(
        component.materialIds,
        flowEdges,
        materialNodes,
      ).filter((leaf) => !isReminderNode(leaf.id!, flowEdges, operationNodes, materialNodes));
      candidates.push(...leaves);
    } else {
      // Leaf → link to roots of other components (excluding reminder nodes)
      const roots = getRootMaterials(
        component.materialIds,
        flowEdges,
        materialNodes,
      ).filter((root) => !isReminderNode(root.id!, flowEdges, operationNodes, materialNodes));
      candidates.push(...roots);
    }
  }

  return candidates;
}

/**
 * Find candidate merge targets for a given material endpoint.
 *
 * For merge, we need materials in DIFFERENT components that have OPPOSITE endpoint types:
 * - ROOT clicked: candidates are NON-ROOT (intermediate or leaf) materials from other components
 * - LEAF clicked: candidates are NON-LEAF (intermediate or root) materials from other components
 *
 * Excludes reminder nodes (neither as source nor target for merge).
 */
export function getMergeCandidates(
  materialId: string,
  contextType: 'ROOT' | 'LEAF',
  graphData: Pick<
    GraphRecords,
    'materialNodes' | 'operationNodes' | 'flowEdges' | 'groups'
  >,
): MaterialNodeSpec[] {
  const { materialNodes, operationNodes, flowEdges, groups } = graphData;

  // Find which component this material belongs to
  const components = getConnectedComponents({ materialNodes, operationNodes, flowEdges });
  const currentComponentId = components.find((c) =>
    c.materialIds.has(materialId),
  )?.componentId;

  if (!currentComponentId) {
    return [];
  }

  // Collect group step IDs (to exclude group members)
  const groupStepIds = new Set<string>();
  if (groups) {
    for (const group of Object.values(groups)) {
      if (group.stepIds) {
        for (const stepId of group.stepIds) {
          groupStepIds.add(stepId);
        }
      }
    }
  }

  const candidates: MaterialNodeSpec[] = [];

  for (const component of components) {
    if (component.componentId === currentComponentId) {
      continue; // Skip same component
    }

    // Filter: non-reminder, not in group, and has opposite endpoint type
    const candidatesInComponent = Array.from(component.materialIds)
      .map((matId) => materialNodes[matId])
      .filter((m) => m != null)
      .filter((m) => !isReminderNode(m.id!, flowEdges, operationNodes, materialNodes))
      .filter((m) => !groupStepIds.has(m.id!));

    if (contextType === 'ROOT') {
      // Clicked is ROOT → candidates must be LEAF (opposite endpoint type)
      const leaves = candidatesInComponent.filter((m) => isLeafMaterial(m.id!, flowEdges));
      candidates.push(...leaves);
    } else {
      // Clicked is LEAF → candidates must be ROOT (opposite endpoint type)
      const roots = candidatesInComponent.filter((m) => isRootMaterial(m.id!, flowEdges));
      candidates.push(...roots);
    }
  }

  return candidates;
}
